import React, { useEffect } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import managementReviewService from '@/services/managementReview.service';
import { apiErrorMessage } from '@/utils/apiErrorMessage';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';

const { Title, Text } = Typography;
const { TextArea } = Input;

type ParticipantRow = {
  key: string;
  name: string;
  designation?: string;
  participant_role?: string;
};

const ManagementReviewFormPage: React.FC<{ mode?: 'create' | 'edit' }> = ({ mode }) => {
  const { id } = useParams();
  const isNew = mode === 'create' || !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form] = Form.useForm();

  const { data, isLoading } = useQuery({
    queryKey: ['management-reviews', id],
    queryFn: () => managementReviewService.get(id!),
    enabled: !isNew && !!id,
  });

  const { data: priorList = [] } = useQuery({
    queryKey: ['management-reviews', 'closed-prior'],
    queryFn: () => managementReviewService.list({ status: 'Closed' }),
  });

  useEffect(() => {
    if (!data || isNew) return;
    form.setFieldsValue({
      department_id: data.department_id || undefined,
      previous_mrm_id: data.previous_mrm_id || undefined,
      proposed_date: data.proposed_date ? dayjs(data.proposed_date) : undefined,
      proposed_time: data.proposed_time || undefined,
      venue: data.venue || undefined,
      agenda_prepared_by_name: data.agenda_prepared_by_name || undefined,
      agenda_prepared_by_designation: data.agenda_prepared_by_designation || undefined,
      agenda_prepared_date: data.agenda_prepared_date ? dayjs(data.agenda_prepared_date) : undefined,
      notes: data.notes || undefined,
      participants: (data.participants || [])
        .filter((p) => String(p.participant_role || 'required') === 'required')
        .map((p, i) => ({
          key: p.id || `p-${i}`,
          name: p.name,
          designation: p.designation || '',
          participant_role: 'required',
        })),
    });
  }, [data, form, isNew]);

  const save = useMutation({
    mutationFn: async (values: any) => {
      const participants: ParticipantRow[] = values.participants || [];
      const payload = {
        department_id: values.department_id || null,
        previous_mrm_id: values.previous_mrm_id || null,
        proposed_date: values.proposed_date ? values.proposed_date.format('YYYY-MM-DD') : null,
        proposed_time: values.proposed_time || null,
        venue: values.venue || null,
        agenda_prepared_by_name: values.agenda_prepared_by_name || null,
        agenda_prepared_by_designation: values.agenda_prepared_by_designation || null,
        agenda_prepared_date: values.agenda_prepared_date
          ? values.agenda_prepared_date.format('YYYY-MM-DD')
          : null,
        notes: values.notes || null,
        participants: participants
          .filter((p) => String(p.name || '').trim())
          .map((p, i) => ({
            name: String(p.name).trim(),
            designation: p.designation || null,
            participant_role: 'required',
            sort_order: i,
          })),
      };
      if (isNew) return managementReviewService.create(payload);
      return managementReviewService.update(id!, payload);
    },
    onSuccess: (saved) => {
      message.success(isNew ? 'MRM created' : 'MRM updated');
      qc.invalidateQueries({ queryKey: ['management-reviews'] });
      navigate(`/qms/management-reviews/${saved.id}`);
    },
    onError: (e) => message.error(apiErrorMessage(e)),
  });

  if (!isNew && data && data.status !== 'Draft') {
    return (
      <div style={{ padding: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/qms/management-reviews/${id}`)}>
          Back
        </Button>
        <Card style={{ marginTop: 16 }}>
          <Text>
            Agenda header can only be edited in Draft. Current status: <Text strong>{data.status}</Text>.
            Use the meeting workspace for minutes and actions.
          </Text>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/management-reviews')}>
          Back
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {isNew ? 'New Management Review Meeting' : `Edit ${data?.mrm_number || ''}`}
        </Title>
      </Space>

      <Form
        form={form}
        layout="vertical"
        onFinish={(v) => save.mutate(v)}
        initialValues={{ participants: [{ key: '1', name: '', designation: '', participant_role: 'required' }] }}
        disabled={isLoading || save.isPending}
      >
        <Card title="MRM Details" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="department_id" label="Department (MR owner)">
                <DepartmentSelect allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="previous_mrm_id" label="Previous MRM (carry open actions)">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={priorList.map((p) => ({
                    value: p.id,
                    label: `${p.mrm_number}${p.meeting_date ? ` (${dayjs(p.meeting_date).format('DD/MM/YYYY')})` : ''}`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="proposed_date" label="Proposed Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="proposed_time" label="Time">
                <Input placeholder="e.g. 10:00 AM" />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item name="venue" label="Location / Venue" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Required Participants" style={{ marginBottom: 16 }}>
          <Form.List name="participants">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Row gutter={8} key={field.key} style={{ marginBottom: 8 }} align="middle">
                    <Col xs={24} md={10}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'name']}
                        rules={[{ required: true, message: 'Name required' }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Name" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={10}>
                      <Form.Item {...field} name={[field.name, 'designation']} style={{ marginBottom: 0 }}>
                        <Input placeholder="Designation" />
                      </Form.Item>
                    </Col>
                    <Col>
                      <Button danger type="link" onClick={() => remove(field.name)} disabled={fields.length <= 1}>
                        Remove
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ name: '', designation: '', participant_role: 'required' })} block>
                  Add participant
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Card title="Agenda prepared by (MR)" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="agenda_prepared_by_name" label="Name">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="agenda_prepared_by_designation" label="Designation">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="agenda_prepared_date" label="Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label="Notes">
                <TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={save.isPending}>
            Save
          </Button>
          <Button onClick={() => navigate('/qms/management-reviews')}>Cancel</Button>
        </Space>
      </Form>
    </div>
  );
};

export default ManagementReviewFormPage;
